import RolesModal, { RoleState } from '@app/components/userManagement/organizationUsersTable/RolesModal';
import { OrganizationalUser, transformOrganizationalUser } from '@app/domain/userManagement/organizationUsers';
import { UserRoleType } from '@app/domain/userManagement/roleTypes';
import { updateUserRoles } from '@app/services/userManagement/organizationUsersService';
import Loadable from '@app/utils/Loadable';
import { shoot } from '@app/utils/messageLauncher';
import React, { FC } from 'react';

interface Props {
  organizationalUser: OrganizationalUser;
  organizationId: string;
  userRoleTypes: Loadable<UserRoleType[]>;
  canEdit: boolean;
  onUserUpdated(organizationalUser: OrganizationalUser);
  onDone: () => void;
}

const UserPermissionsModal: FC<Props> = ({
  organizationalUser,
  organizationId,
  userRoleTypes,
  canEdit,
  onUserUpdated,
  onDone,
}) => {
  const { tenantUserId, globalAppRoleIds, allActiveAppRoleIds } = organizationalUser;

  function getRoleState(roleId: string, roleType: UserRoleType | undefined): RoleState {
    if (globalAppRoleIds.includes(roleId)) {
      return { isDisabled: true, tooltip: 'This role is a global role and therefore cannot be unassigned' };
    }

    if (!roleType) {
      return {
        isDisabled: false,
        tooltip: 'This role is assigned although the organization does not have the proper service for it',
      };
    }

    return { isDisabled: false, tooltip: null };
  }

  return (
    <RolesModal
      title={<>Permissions - {organizationalUser.name}</>}
      elementIds={{
        card: 'add-users-to-org-modal',
        search: 'user-manage-roles-search',
        table: 'user-manage-roles-table',
        close: 'btn-user-permissions-modal-close',
        cancel: 'btn-user-permissions-modal-cancel',
        save: 'btn-user-permissions-modal-save',
      }}
      contentColumnTitle='Roles'
      roleTypes={userRoleTypes}
      initialActiveRoleIds={allActiveAppRoleIds}
      canEdit={canEdit}
      getRoleState={getRoleState}
      saveChange={async (change): Promise<void> => {
        onUserUpdated(transformOrganizationalUser(await updateUserRoles(organizationId, tenantUserId, change)));
        shoot({ type: 'success', closeable: true }, `Permissions of "${organizationalUser.name}" saved`);
      }}
      onDone={onDone}
    />
  );
};

export default UserPermissionsModal;
